import { SortingState } from "@tanstack/react-table";
import snakeCase from "lodash/snakeCase";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import api from "PFCore/api";
import { RequestConfig } from "PFCore/api/api";
import { decamelizeDesiredKeys } from "PFCore/helpers/keys_camelization_utils";
import {
  AuditPlannerException,
  AuditPlannerExceptionsDictionary,
  AuditRole,
  AutoAllocationSettings,
  Collection,
  Engagement,
  Id,
  PaginatedCollection,
  QueryParams,
  WorkflowAutomation
} from "PFTypes";
import { AllocationWindow } from "PFTypes/target_allocation";

export type AuditRoleItem = {
  id: Id;
  activity: AuditRole;
};

export type AuditRolesCollection = PaginatedCollection<AuditRoleItem[]>;

export const fetchAuditRoles = ({
  page = 1,
  perPage = 25,
  filters,
  order
}: QueryParams): Promise<AuditRolesCollection> =>
  api({
    url: "audit_roles",
    params: {
      filters,
      perPage: perPage,
      page: page,
      order
    }
  });

export const approveExceptionRequest = (
  shortlistId: number,
  approverId: number,
  reason: string
): Promise<Collection<AuditPlannerException[]>> =>
  api({
    url: "exception_requests/approve",
    method: "PUT",
    data: { shortlistId, approverId, reason }
  });

export const rejectExceptionRequest = (
  shortlistId: number,
  approverId: number,
  reason: string
): Promise<Collection<AuditPlannerException[]>> =>
  api({
    url: "exception_requests/reject",
    method: "PUT",
    data: { shortlistId, approverId, reason }
  });

export type ExceptionRequestBulk = { shortlistIds: number[]; reason: string };
export const approveExceptionRequestBulk = ({
  shortlistIds,
  reason
}: ExceptionRequestBulk): Promise<Collection<AuditPlannerException[]>> =>
  api({
    url: "exception_requests/bulk_approve",
    method: "POST",
    data: { shortlistIds, reason }
  });

export const rejectExceptionRequestBulk = ({
  shortlistIds,
  reason
}: ExceptionRequestBulk): Promise<Collection<AuditPlannerException[]>> =>
  api({
    url: "exception_requests/bulk_reject",
    method: "POST",
    data: { shortlistIds, reason }
  });

export const editExceptionRequest = (
  shortlistId: number,
  approverId: number
): Promise<Collection<AuditPlannerException[]>> =>
  api({
    url: "exception_requests",
    method: "PUT",
    data: { approverId, shortlistId }
  });

export const fetchExceptionRequests = (
  activityId: number,
  profileIds: number | number[]
): Promise<Collection<AuditPlannerException[]>> =>
  api({
    url: "exception_requests",
    params: {
      activityId,
      profileIds
    }
  });

export const getWorkflowAutomation = (): Promise<WorkflowAutomation> =>
  api({
    url: "workflow_automation"
  });

export const runWorkflowAutomation = (): Promise<WorkflowAutomation> =>
  api({
    url: "workflow_automation",
    method: "POST"
  });

export const fetchAutoAllocationSettings = (): Promise<AutoAllocationSettings> =>
  api({
    url: "auto_allocation_settings"
  });

export const setAutoAllocationSettings = (alwaysOn: boolean): Promise<AutoAllocationSettings> =>
  api({
    url: "auto_allocation_settings",
    method: "PUT",
    data: {
      alwaysOn
    }
  });

export type AllocationWindowsParams = {
  page?: number;
  perPage?: number;
  sort?: SortingState;
  termValue?: string;
};

const getOrder = (sort?: SortingState) => {
  if (!sort) {
    return undefined;
  }

  const snakifiedId = snakeCase(sort[0].id);

  return {
    name: [snakifiedId],
    direction: sort[0].desc ? "desc" : "asc"
  };
};

export const fetchAllocationWindow = (allocationWindowId: Id): Promise<AllocationWindow> =>
  api({
    url: `audit_planner/allocation_windows/${allocationWindowId}`,
    method: "GET"
  });

export const fetchAllocationWindows = ({
  page = 1,
  perPage = PER_PAGE_UNPAGINATED,
  sort,
  termValue
}: AllocationWindowsParams): Promise<PaginatedCollection<AllocationWindow[]>> =>
  api({
    url: "audit_planner/allocation_windows",
    method: "GET",
    params: {
      page,
      perPage,
      order: getOrder(sort),
      term: termValue
    }
  });

type AuditEngagementItem = {
  activity: Engagement;
  id: number;
};

export type AuditEngagementCollection = PaginatedCollection<AuditEngagementItem[]>;

export const fetchAuditEngagements = (
  data: Omit<QueryParams, "perPage"> & { perPage: number },
  config?: RequestConfig
): Promise<AuditEngagementCollection> =>
  api({
    url: "audit_engagements/index",
    method: "POST",
    data,
    ...(config ?? {})
  }).then((res) => decamelizeDesiredKeys<AuditEngagementCollection>(res, ["entries", "meta.counters"]));

export type PotentialExceptionsParams = {
  profileId: number;
  activityId: number;
  bookingsIds?: number[];
};

export const fetchPotentialExceptions = (
  data: PotentialExceptionsParams
): Promise<AuditPlannerExceptionsDictionary> =>
  api({
    url: "audit_planner/potential_exceptions/check",
    method: "POST",
    data
  });

export const resetAuditEngagements = (engagementIds: Id[]): Promise<void> =>
  api({
    url: "audit_engagements/reset",
    method: "POST",
    data: { engagementIds }
  });
